import { useMemo, useRef } from "react";

import { useDispatch, useStore } from "react-redux";

import type { ILegacyIndustryFilterDto } from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import {
    setExpandedFilterName,
    setFilterMode,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import {
    INDUSTRY_TAXONOMY_ACCESS_REQUEST_FEATURE,
    INDUSTRY_TAXONOMY_DEMO_COPY_NAMESPACE,
    INDUSTRY_TAXONOMY_DEMO_ID,
} from "app/pages/sales-intelligence/modules/industry/constants";
import { useIndustryQueryClient } from "app/pages/sales-intelligence/modules/industry/hooks/useIndustryQueryClient";
import { resetTabsPagination } from "app/pages/sales-intelligence/modules/leads-search/store/action-creators";
import type { IDemoSession } from "app/pages/sales-intelligence/types/demo-mode";
import type { RootState } from "single-spa/store/types";

import { updateLeadsFilterValue } from "../store/action-creators";
import { selectFiltersValue } from "../store/selectors";

/**
 * Builds the industry-taxonomy demo session. `onEnter`/`onExit` keep the *durable* filters slice a pure
 * function of state (never a demo flag in the DTO selector): entering clears any legacy `industryFilter` so it
 * cannot leak into the search request, and exiting clears the v2 `industryTaxonomy` selection. Both models treat
 * `undefined` as their empty value, so the resolver omits the key. `dispatch` and `store` are stable, so the
 * returned session's `onExit` remains valid even after the component that entered the demo unmounts.
 *
 * The clear at enter would otherwise destroy the user's legacy selection, so the displaced value is stashed
 * and written back on exit — but only into an empty slot, never over a value some other writer (a saved
 * search, a list overlay) put there since.
 *
 * Both transitions also reset pagination: crossing into or out of the demo swaps the result set, so the page
 * the user was on no longer refers to anything.
 */
export const useIndustryTaxonomyDemoSession = (): IDemoSession => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const industryQueryClient = useIndustryQueryClient();

    const displacedLegacyValue = useRef<ILegacyIndustryFilterDto | undefined>(undefined);

    return useMemo(
        () => ({
            id: INDUSTRY_TAXONOMY_DEMO_ID,
            copyNamespace: INDUSTRY_TAXONOMY_DEMO_COPY_NAMESPACE,
            accessRequestFeature: INDUSTRY_TAXONOMY_ACCESS_REQUEST_FEATURE,
            onEnter: () => {
                displacedLegacyValue.current = selectFiltersValue(store.getState()).leadsFilters?.industryFilter;
                dispatch(updateLeadsFilterValue({ leadsFilters: { industryFilter: undefined } }));

                // Entering swaps the result set, so page N of the old one is meaningless (and may not exist).
                dispatch(resetTabsPagination());

                // If the user had switched to the agent tab before entering the demo, the expansion below would
                // land in a `display: none` panel — and that tab is disabled for the duration of a demo.
                dispatch(setFilterMode("filters-list"));

                // Open the v2 card immediately. Safe against the panel's collapse-if-not-visible guard:
                // this runs in the same event as the demo-id flip, so the v2 card (pending placeholder or
                // cached live card) enters the list in the same commit as this expansion.
                dispatch(setExpandedFilterName("industryTaxonomy"));
            },
            onExit: () => {
                // Once the account is on the taxonomy the legacy card no longer renders, so restoring
                // its value would apply a filter the user can neither see nor remove.
                const keepsLegacyClassification = !industryQueryClient.readIsTaxonomyOptedIn();
                const isSlotFree = !selectFiltersValue(store.getState()).leadsFilters?.industryFilter;
                const restored = keepsLegacyClassification && isSlotFree ? displacedLegacyValue.current : undefined;
                displacedLegacyValue.current = undefined;

                // Omitting the key (vs. sending `undefined`) is what leaves a foreign value untouched.
                dispatch(
                    updateLeadsFilterValue({
                        leadsFilters: {
                            industryTaxonomy: undefined,
                            ...(restored && { industryFilter: restored }),
                        },
                    }),
                );

                // Leaving swaps the result set back, so the demo's page N does not carry over.
                dispatch(resetTabsPagination());
            },
        }),
        [dispatch, industryQueryClient, store],
    );
};
